/**
 * Product Variant Pricing Extension
 * Handles price display updates for product variant options
 */
class VariantPricing {
  constructor() {
    this.productData = this.getProductData();
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateInitialPrices();
  }

  getProductData() {
    // Try to get product data from various sources
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        return JSON.parse(productJson.innerHTML);
      } catch (e) {
        console.warn('Could not parse product JSON:', e);
      }
    }
    
    // Fallback: try window.productData
    if (window.productData) {
      return window.productData;
    }

    return null;
  }

  bindEvents() {
    // Listen for option changes on popout selectors
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-popout-input]') || e.target.matches('[data-single-option-selector]')) {
        this.updateTogglePricing(e.target);
      }
    });

    // Listen for popout option clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-popout-option]')) {
        const option = e.target.closest('[data-popout-option]');
        const popout = option.closest('popout-select');
        if (popout) {
          // Small delay to ensure the popout has updated
          setTimeout(() => {
            this.updateTogglePricingFromPopout(popout, option);
          }, 10);
        }
      }
    });
  }

  updateInitialPrices() {
    // Update initial prices for all popout toggles
    const popouts = document.querySelectorAll('popout-select');
    popouts.forEach(popout => {
      this.updateTogglePricingFromPopout(popout);
    });
  }

  updateTogglePricingFromPopout(popout, selectedOption = null) {
    const togglePrice = popout.querySelector('[data-popout-toggle-price]');
    if (!togglePrice) return;

    // Get selected option
    const option = selectedOption || popout.querySelector('[data-popout-option][aria-current="true"]') || 
                   popout.querySelector('.select-popout__item.is-active [data-popout-option]');
    
    if (option) {
      const priceElement = option.querySelector('.select-option__price');
      if (priceElement) {
        togglePrice.textContent = priceElement.textContent;
        togglePrice.style.display = 'inline';
      } else {
        togglePrice.style.display = 'none';
      }
    }
  }

  updateTogglePricing(input) {
    const popout = input.closest('popout-select');
    if (!popout) return;

    const togglePrice = popout.querySelector('[data-popout-toggle-price]');
    if (!togglePrice) return;

    // Find the variant that matches the selected options
    const variant = this.findVariantBySelectedOptions();
    if (variant && variant.price) {
      const formattedPrice = this.formatPrice(variant.price);
      togglePrice.textContent = formattedPrice;
      togglePrice.style.display = 'inline';
    } else {
      togglePrice.style.display = 'none';
    }
  }

  findVariantBySelectedOptions() {
    if (!this.productData || !this.productData.variants) return null;

    const selectedOptions = this.getSelectedOptions();
    if (!selectedOptions || selectedOptions.length === 0) return null;

    return this.productData.variants.find(variant => {
      return variant.options.every((option, index) => 
        option === selectedOptions[index]
      );
    });
  }

  getSelectedOptions() {
    const options = [];
    const selectors = document.querySelectorAll('[data-single-option-selector]');
    
    selectors.forEach(selector => {
      if (selector.type === 'radio' && !selector.checked) return;
      if (selector.value) {
        options.push(selector.value);
      }
    });

    return options;
  }

  formatPrice(price) {
    // Use Shopify's money formatting if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(price);
    }
    
    // Fallback formatting
    const priceInDollars = price / 100;
    return `$${priceInDollars.toFixed(2)}`;
  }

  // Static method to initialize when DOM is ready
  static init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        new VariantPricing();
      });
    } else {
      new VariantPricing();
    }
  }
}

// Initialize the pricing functionality
VariantPricing.init();

// Export for potential use elsewhere
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VariantPricing;
} else if (typeof window !== 'undefined') {
  window.VariantPricing = VariantPricing;
}
